import { Injectable, inject } from '@angular/core';
import { ToastController } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { checkmarkCircleOutline, informationCircleOutline, alertCircleOutline, warningOutline, closeOutline } from 'ionicons/icons';

/** Duration in milliseconds for standard toasts. */
const TOAST_DURATION_MS = 2200;

/** Maps colour key to the Ionicons icon name shown on each toast type. */
const TOAST_ICONS: Record<string, string> = {
  success: 'checkmark-circle-outline',
  dark:    'information-circle-outline',
  danger:  'alert-circle-outline',
  warning: 'warning-outline',
};

/**
 * Wraps Ionic's `ToastController` to provide one-line success, info, and
 * error notifications consistent with the Titanium Legal brand.
 *
 * @remarks
 * Toasts appear at the top of the screen below the status bar, matching
 * the prototype's `.toast` positioning. Calls are serialised through an
 * internal queue — see {@link enqueue} — so only one toast is ever visible
 * at a time; a second call made while one is showing waits for it to fully
 * dismiss (auto-timeout or a manual close-button tap) before presenting.
 * This prevents overlapping/stacked toasts when multiple independent
 * failures are reported in quick succession (e.g. several API calls in the
 * same sync cycle each failing).
 *
 * @example
 * ```ts
 * const toast = inject(ToastService);
 * await toast.success('Data refreshed');
 * await toast.error('Failed to load form definition.');
 * ```
 */

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly ctrl = inject(ToastController);

  /**
   * Tail of the serial toast queue. Each call to {@link enqueue} chains its
   * work onto this promise so toasts always present one at a time, in the
   * order they were requested.
   */
  private queueTail: Promise<void> = Promise.resolve();

  constructor() {
    addIcons({ checkmarkCircleOutline, informationCircleOutline, alertCircleOutline, warningOutline, closeOutline });
  }

  /**
   * Shows a green success toast.
   *
   * @param message - The message to display.
   */
  async success(message: string): Promise<void> {
    await this.enqueue(message, 'success');
  }

  /**
   * Shows a dark informational toast (brand green background).
   *
   * @param message - The message to display.
   */
  async info(message: string): Promise<void> {
    await this.enqueue(message, 'dark');
  }

  /**
   * Shows a yellow warning toast.
   *
   * @param message  - The message to display.
   * @param anchorId - Optional element ID to anchor the toast just above (e.g.
   *                   `'main-tab-bar'`). When provided the toast uses
   *                   `position: 'bottom'` so it appears above that element
   *                   rather than at the top of the screen.
   */
  async warning(message: string, anchorId?: string): Promise<void> {
    await this.enqueue(message, 'warning', anchorId);
  }

  /**
   * Shows a red danger toast.
   *
   * @param message  - The message to display.
   * @param anchorId - Optional element ID to anchor the toast just above (e.g.
   *                   `'main-tab-bar'`). When provided the toast uses
   *                   `position: 'bottom'` so it appears above that element
   *                   rather than at the top of the screen.
   */
  async error(message: string, anchorId?: string): Promise<void> {
    await this.enqueue(message, 'danger', anchorId);
  }

  /**
   * Shows a red danger toast at the top of the screen that stays visible
   * until the user taps its close button — it does not auto-dismiss after
   * {@link TOAST_DURATION_MS} like {@link error}. Used for failures the
   * process server must actively acknowledge (e.g. a failed print/email
   * request) rather than a passing notification.
   *
   * @param message - The message to display.
   */
  async errorUntilDismissed(message: string): Promise<void> {
    await this.enqueue(message, 'danger', undefined, { persistent: true });
  }

  /**
   * Chains a toast request onto {@link queueTail} so it waits its turn behind
   * any toast already showing (or already queued), then shows it via {@link show}.
   *
   * @remarks
   * `queueTail` is replaced with the new task on every call — including a
   * `.catch()` so a failure in one queued toast (e.g. `ctrl.create()`
   * rejecting) never leaves the queue permanently stuck, blocking every
   * subsequent toast forever.
   *
   * @param message  - Text content of the toast.
   * @param color    - Colour key — maps to the `app-toast-{color}` CSS class.
   * @param anchorId - Optional anchor element ID (see {@link show}).
   * @param options  - Optional display options (see {@link show}).
   */
  private enqueue(
    message: string,
    color: string,
    anchorId?: string,
    options?: { persistent?: boolean },
  ): Promise<void> {
    const task = this.queueTail.then(() => this.show(message, color, anchorId, options));
    this.queueTail = task.catch(() => void 0);
    return task;
  }

  /**
   * Creates, presents, and waits for the dismissal of an Ionic toast with the
   * given colour class.
   *
   * @remarks
   * The `color` prop on `ion-toast` does not resolve CSS variables when the
   * element is appended outside the component shadow DOM. We use `cssClass`
   * instead and define explicit CSS custom properties in `src/styles.scss`.
   *
   * Resolves only after the toast is actually dismissed (auto-timeout for a
   * standard toast, or the user tapping the close button for a
   * `persistent` one) — see {@link enqueue}, which relies on this to keep
   * toasts from overlapping.
   *
   * @param message  - Text content of the toast.
   * @param color    - Colour key — maps to the `app-toast-{color}` CSS class.
   * @param anchorId - Optional element ID; when set the toast floats just above
   *                   that element (`position: 'bottom'`). When omitted, no
   *                   `positionAnchor` is passed and Ionic defaults to the top.
   * @param options.persistent - When `true`, omits `duration` (so the toast
   *                   never auto-dismisses) and adds a close button the user
   *                   must tap to dismiss it.
   */
  private async show(
    message: string,
    color: string,
    anchorId?: string,
    options?: { persistent?: boolean },
  ): Promise<void> {
    const toast = await this.ctrl.create({
      message,
      ...(options?.persistent ? {} : { duration: TOAST_DURATION_MS }),
      cssClass:  `app-toast-${color}`,
      icon:      TOAST_ICONS[color],
      position:  anchorId ? 'bottom' : 'top',
      ...(anchorId ? { positionAnchor: anchorId } : {}),
      ...(options?.persistent
        ? { buttons: [{ icon: 'close-outline', role: 'cancel' }] }
        : {}),
    });

    // Ionic's .toast-content has hardcoded padding:15px in its shadow DOM — not a CSS
    // custom property, so it cannot be overridden from outside. Patch it after render
    // to match the prototype's compact 8px top/bottom padding.
    toast.addEventListener?.('ionToastWillPresent', () => {
      const inner = toast.shadowRoot?.querySelector<HTMLElement>('.toast-content');
      if (inner) {
        inner.style.setProperty('padding-top', '8px');
        inner.style.setProperty('padding-bottom', '8px');
      }
    });

    await toast.present();
    await toast.onDidDismiss();
  }
}
